'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const CART_KEY = 'travogue_cart';
const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(CART_KEY);
      if (stored) {
        setItems(JSON.parse(stored));
      }
    } catch {
      // localStorage may be unavailable in SSR or private browsing
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded) {
      return;
    }
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(items));
    } catch {
      // localStorage may be unavailable in SSR or private browsing
    }
  }, [items, loaded]);

  const addItem = useCallback((item) => {
    setItems((prev) => {
      const exists = prev.find((i) => i.sessionId === item.sessionId);
      if (exists) {
        return prev.map((i) => (i.sessionId === item.sessionId ? { ...i, ...item } : i));
      }
      return [...prev, { quantity: 1, ...item }];
    });
  }, []);

  const removeItem = useCallback((sessionId) => {
    if (!sessionId) {
      return;
    }
    setItems((prev) => prev.filter((i) => i.sessionId !== sessionId));
  }, []);

  const updateQuantity = useCallback((sessionId, quantity) => {
    setItems((prev) =>
      prev.map((i) =>
        i.sessionId === sessionId ? { ...i, quantity: Math.max(1, Math.min(20, quantity)) } : i,
      ),
    );
  }, []);

  const updateItem = useCallback((sessionId, fields) => {
    setItems((prev) => prev.map((i) => (i.sessionId === sessionId ? { ...i, ...fields } : i)));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    // Чистим и localStorage веднага — иначе при пълно зареждане (redirect към thank-you)
    // ефектът за хидратация може да възстанови количката след clearCart.
    try {
      localStorage.removeItem(CART_KEY);
    } catch {
      // localStorage may be unavailable
    }
  }, []);

  const contextValue = useMemo(
    () => ({ items, addItem, removeItem, updateQuantity, updateItem, clearCart }),
    [items, addItem, removeItem, updateQuantity, updateItem, clearCart],
  );

  return <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart must be used within CartProvider');
  }
  return ctx;
}
